import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MaterialIssue } from '../../models/inventory/material-issue';
import { MaterialIssueSearchCommand } from '../../models/inventory/material-issue-search';
import { ApiResponse, EntityKeyByReference } from '@card-software/library';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class MaterialIssueProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/material-issue';

  public getByEntityKey(entityKey: EntityKeyByReference, skipInterceptor = false): Observable<ApiResponse<MaterialIssue>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<MaterialIssue>>(this.baseUrl + '/entity-key', entityKey, { headers });
  }

  /**
   * Crea un nuevo MaterialIssue desde un Dispatch Request
   * @param entityKey - EntityKey del Dispatch Request
   * @param locationCode - Código de ubicación desde donde se despachará
   */
  public createFromEntityKey(
    entityKey: EntityKeyByReference,
    locationCode: string,
    skipInterceptor = false,
  ): Observable<ApiResponse<MaterialIssue>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<MaterialIssue>>(
      `${this.baseUrl}/entity-key?locationCode=${encodeURIComponent(locationCode)}`,
      entityKey,
      {
        headers,
      },
    );
  }

  public search(searchCommand: MaterialIssueSearchCommand, skipInterceptor = false): Observable<ApiResponse<MaterialIssue[]>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<MaterialIssue[]>>(this.baseUrl + '/search', searchCommand, { headers });
  }

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<MaterialIssue>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<MaterialIssue>>(`${this.baseUrl}/${id}`, { headers });
  }

  public create(body: MaterialIssue): Observable<ApiResponse<MaterialIssue>> {
    return this.httpClient.post<ApiResponse<MaterialIssue>>(this.baseUrl, body);
  }

  public update(id: string, body: MaterialIssue): Observable<ApiResponse<MaterialIssue>> {
    return this.httpClient.put<ApiResponse<MaterialIssue>>(`${this.baseUrl}/${id}`, body);
  }

  /**
   * Actualiza solo el estado del MaterialIssue
   * @param id - ID del MaterialIssue
   * @param newStatus - Nuevo estado
   */
  public updateStatus(id: string, newStatus: number): Observable<ApiResponse<MaterialIssue>> {
    return this.httpClient.put<ApiResponse<MaterialIssue>>(`${this.baseUrl}/status/${id}`, newStatus);
  }

  public delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }
}
